import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goalsApi, type CreateGoalRequest, type UpdateGoalRequest } from '@/lib/api/goals';
import { toast } from 'sonner';

/**
 * Fetch goals for a specific employee
 */
export function useGoals(employeeId: string) {
  return useQuery({
    queryKey: ['goals', employeeId],
    queryFn: () => goalsApi.getEmployeeGoals(employeeId),
    enabled: !!employeeId,
  });
}

/**
 * Fetch single goal details
 */
export function useGoalDetails(goalId: string | null) {
  return useQuery({
    queryKey: ['goal', goalId],
    queryFn: () => goalsApi.getGoal(goalId!),
    enabled: !!goalId,
  });
}

/**
 * Create new goal mutation
 */
export function useCreateGoal(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGoalRequest) =>
      goalsApi.createGoal(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', employeeId] });
      toast.success('Goal created successfully');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Failed to create goal';
      toast.error(message);
    },
  });
}

/**
 * Update goal mutation
 */
export function useUpdateGoal(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: UpdateGoalRequest }) =>
      goalsApi.updateGoal(goalId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['goals', employeeId] });
      queryClient.invalidateQueries({ queryKey: ['goal', variables.goalId] });
      toast.success('Goal updated successfully');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Failed to update goal';
      toast.error(message);
    },
  });
}

/**
 * Delete goal mutation
 */
export function useDeleteGoal(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) => goalsApi.deleteGoal(goalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', employeeId] });
      toast.success('Goal deleted successfully');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message || 'Failed to delete goal';
      toast.error(message);
    },
  });
}

/**
 * Fetch goal change history
 */
export function useGoalHistory(goalId: string | null) {
  return useQuery({
    queryKey: ['goal-history', goalId],
    queryFn: () => goalsApi.getGoalHistory(goalId!),
    enabled: !!goalId,
  });
}
